<template>
  <div class="analytics-container">
    <div class="analytics-header">
      <BotEditorSubHeader
        :title="$t('botEditor.analytics.title')"
        :subtitle="$t('botEditor.analytics.subtitle')"
      />
    </div>

    <!-- Analytics Source Toggle (visibile solo se bot ha sia test che production) -->
    <div v-if="hasMultipleVersions" class="mb-6 flex items-center gap-4 bg-slate-800/50 p-4 rounded-lg border border-slate-700">
      <label class="text-sm font-medium text-gray-300">
        {{ $t('botEditor.analytics.source.label') }}
      </label>
      
      <SelectButton
        v-model="analyticSource"
        :options="analyticSourceOptions"
        optionLabel="label"
        optionValue="value"
        :allowEmpty="false"
      />
    </div>

    <!-- Date Range Selector -->
    <div class="mb-4">
      <AnalyticsDateRange 
        v-model:range="selectedRange"
        v-model:customFrom="customFrom"
        v-model:customTo="customTo"
        @update="fetchAnalytics"
      />
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center text-gray-400 py-8">
      <ProgressSpinner
        style="width: 2.5rem; height: 2.5rem; margin-bottom: 0.75rem"
        stroke-width="3"
        :aria-label="$t('common.loading')"
      />
      <p class="text-sm">{{ $t('botEditor.analytics.loadingText') }}</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="bg-red-900/20 border border-red-500 rounded-lg p-4 text-center">
      <span class="material-icons text-red-400">warning</span>
      <p class="text-red-400 text-sm">{{ error }}</p>
      <Button
        severity="danger"
        :label="$t('botEditor.analytics.retryButton')"
        class="mt-3"
        @click="fetchAnalytics"
      />
    </div>

    <!-- Analytics Content -->
    <div v-else-if="analytics">
      <!-- Summary Cards -->
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <!-- Total Conversations -->
        <div class="stat-card">
          <div class="stat-icon bg-blue-500/20">
            <span class="material-icons text-blue-400">forum</span>
          </div>
          <div class="stat-content">
            <p class="stat-label">{{ $t('botEditor.analytics.stats.conversations') }}</p>
            <p class="stat-value">{{ analytics.summary.uniqueSessionsCount }}</p>
          </div>
        </div>

        <!-- Total Messages -->
        <div class="stat-card">
          <div class="stat-icon bg-green-500/20">
            <span class="material-icons text-green-400">message</span>
          </div>
          <div class="stat-content">
            <p class="stat-label">{{ $t('botEditor.analytics.stats.messages') }}</p>
            <p class="stat-value">{{ analytics.summary.totalMessagesReceived + analytics.summary.totalMessagesSent }}</p>
          </div>
        </div>

        <!-- Avg Messages per Conversation -->
        <div class="stat-card">
          <div class="stat-icon bg-purple-500/20">
            <span class="material-icons text-purple-400">sms</span>
          </div>
          <div class="stat-content">
            <p class="stat-label">{{ $t('botEditor.analytics.stats.avgMessagesPerConversation') }}</p>
            <p class="stat-value-small">{{ (analytics.summary.uniqueSessionsCount > 0 ? ((analytics.summary.totalMessagesReceived + analytics.summary.totalMessagesSent) / analytics.summary.uniqueSessionsCount).toFixed(1) : 0) }}</p>
          </div>
        </div>

        <!-- Avg Response Time -->
        <div class="stat-card">
          <div class="stat-icon bg-orange-500/20">
            <span class="material-icons text-orange-400">schedule</span>
          </div>
          <div class="stat-content">
            <p class="stat-label">{{ $t('botEditor.analytics.stats.avgResponseTime') }}</p>
            <p class="stat-value-small">{{ formatDuration(analytics.summary.avgResponseDuration) }}</p>
          </div>
        </div>
      </div>

      <!-- Charts Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <!-- Timeline Chart -->
        <div class="analytics-section">
          <h3 class="section-title">{{ $t('botEditor.analytics.charts.timeline') }}</h3>
          <TimelineChart :data="analytics.timelineTrend" :range="selectedRange" />
        </div>

        <!-- Response Time Chart -->
        <div class="analytics-section">
          <h3 class="section-title">{{ $t('botEditor.analytics.charts.responseTime') }}</h3>
          <ResponseTimeChart :data="analytics.responseTimeTrend" :range="selectedRange" />
        </div>
      </div>

      <!-- Data Tables Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <!-- Top Q&A Matches -->
        <div class="analytics-section">
          <h3 class="section-title">{{ $t('botEditor.analytics.topQa.title') }}</h3>
          <DataTable
            :value="analytics.topQaMatches.slice(0, 5)"
            dataKey="qaId"
            size="small"
            :rowHover="analytics.topQaMatches.length > 0"
            class="text-sm"
          >
            <Column field="question" :header="$t('botEditor.analytics.topQa.questionHeader')" />
            <Column
              field="count"
              :header="$t('botEditor.analytics.topQa.matchesHeader')"
              style="width: 5rem; text-align: center"
              :pt="centeredHeaderPt"
            />
            <Column
              :header="$t('botEditor.analytics.topQa.avgScoreHeader')"
              style="width: 5rem; text-align: center"
              :pt="centeredHeaderPt"
            >
              <template #body="{ data }">
                <Tag :value="formatScore(data.avgMatchScore)" rounded />
              </template>
            </Column>
            <template #empty>
              <div class="text-center py-8 text-gray-500 text-sm">
                <span class="material-icons select-none" aria-hidden="true">help</span>
                <p>{{ $t('botEditor.analytics.topQa.emptyState') }}</p>
              </div>
            </template>
          </DataTable>
        </div>

        <!-- Top Flows -->
        <div class="analytics-section">
          <h3 class="section-title">{{ $t('botEditor.analytics.topFlows.title') }}</h3>
          <DataTable
            :value="analytics.topFlows.slice(0, 5)"
            dataKey="flowId"
            size="small"
            :rowHover="analytics.topFlows.length > 0"
            class="text-sm"
          >
            <Column :header="$t('botEditor.analytics.topFlows.flowNameHeader')">
              <template #body="{ data }">
                <span v-tooltip.top="flowTriggersTooltip(data)" tabindex="0" class="cursor-help">{{ data.flowName }}</span>
              </template>
            </Column>
            <Column
              field="starts"
              :header="$t('botEditor.analytics.topFlows.startsHeader')"
              style="width: 5rem; text-align: center"
              :pt="centeredHeaderPt"
            />
            <Column
              field="completed"
              :header="$t('botEditor.analytics.topFlows.completedHeader')"
              style="width: 6rem; text-align: center"
              :pt="centeredHeaderPt"
            />
            <Column
              :header="$t('botEditor.analytics.topFlows.completionRateHeader')"
              style="width: 7rem; text-align: center"
              :pt="centeredHeaderPt"
            >
              <template #body="{ data }">
                {{ data.completionRate == null ? '—' : `${data.completionRate}%` }}
              </template>
            </Column>
            <template #empty>
              <div class="text-center py-8 text-gray-500 text-sm">
                <span class="material-icons select-none" aria-hidden="true">polyline</span>
                <p>{{ $t('botEditor.analytics.topFlows.emptyState') }}</p>
              </div>
            </template>
          </DataTable>
        </div>
      </div>

      <!-- Errors to fix (full width) -->
      <ErrorsToFix
        :rows="analytics.errorBreakdown || []"
        :workspace-id="workspaceId"
        :bot-id="botId"
        :production-bot-id="analytics.productionBotId"
      />

      <!-- Tools -->
      <div class="grid grid-cols-1 gap-6">
        <!-- Top Tools -->
        <div class="analytics-section">
          <h3 class="section-title text-purple-400">{{ $t('botEditor.analytics.tools.title') }}</h3>
          <DataTable
            :value="topToolRows"
            dataKey="rowKey"
            size="small"
            :rowHover="(analytics.topTools?.length ?? 0) > 0"
            class="text-sm"
          >
            <Column :header="$t('botEditor.analytics.tools.toolNameHeader')">
              <template #body="{ data }">
                <div class="flex flex-col">
                  <span>{{ data.toolName }}</span>
                  <span v-if="data.toolType === 'mcp' && data.serverName" class="text-xs text-gray-500">{{ data.serverName }}</span>
                </div>
              </template>
            </Column>
            <Column
              field="totalCalls"
              :header="$t('botEditor.analytics.tools.callsHeader')"
              style="width: 6rem; text-align: center"
              :pt="centeredHeaderPt"
            />
            <Column
              :header="$t('botEditor.analytics.tools.successRateHeader')"
              style="width: 6rem; text-align: center"
              :pt="centeredHeaderPt"
            >
              <template #body="{ data }">
                <span :class="data.successRate >= 90 ? 'text-green-400' : data.successRate >= 70 ? 'text-yellow-400' : 'text-red-400'">
                  {{ data.successRate }}%
                </span>
              </template>
            </Column>
            <Column
              :header="$t('botEditor.analytics.tools.avgDurationHeader')"
              style="width: 7rem; text-align: center"
              :pt="centeredHeaderPt"
            >
              <template #body="{ data }">
                {{ data.avgDuration == null ? '—' : formatDuration(data.avgDuration) }}
              </template>
            </Column>
            <template #empty>
              <div class="text-center py-8 text-gray-500 text-sm">
                <span class="material-icons text-gray-600 select-none" aria-hidden="true">handyman</span>
                <p>{{ $t('botEditor.analytics.tools.emptyState') }}</p>
              </div>
            </template>
          </DataTable>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="analytics.summary.totalEvents === 0" class="bg-gray-800/50 border border-gray-700 rounded-lg p-8 text-center">
        <span class="material-icons text-gray-600">trending_up</span>
        <p class="text-gray-400">{{ $t('botEditor.analytics.emptyState') }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import api from '../../services/api';
import BotEditorSubHeader from '../../components/ui/BotEditorSubHeader.vue';
import AnalyticsDateRange from '../../components/analytics/AnalyticsDateRange.vue';
import ErrorsToFix from '../../components/analytics/ErrorsToFix.vue';
import TimelineChart from '../../components/analytics/TimelineChart.vue';
import ResponseTimeChart from '../../components/analytics/ResponseTimeChart.vue';

const { t, locale } = useI18n();
const route = useRoute();
const botId = computed(() => route.params.id);
const workspaceId = computed(() => route.params.workspaceId);

const loading = ref(true);
const error = ref(null);
const analytics = ref(null);

const selectedRange = ref('1d');
const customFrom = ref(null);
const customTo = ref(null);

// Nuovo state per il toggle analytics con persistenza localStorage
// null = nessuna scelta salvata: la prima richiesta lascia decidere al server
const analyticSource = ref(null);
// Valore adottato dalla risposta: il watch lo distingue dalla scelta dell'utente
let adoptedSource = null;
const hasMultipleVersions = ref(false);
const botType = ref('test');

const analyticSourceOptions = computed(() => [
  { label: t('botEditor.analytics.source.test'), value: 'test' },
  { label: t('botEditor.analytics.source.production'), value: 'production' },
  { label: t('botEditor.analytics.source.both'), value: 'both' },
]);

const SOURCES = ['test', 'production', 'both'];

/**
 * Carica la sorgente di analytics da localStorage
 * null se non c'è un valore valido (decide il server)
 */
const loadAnalyticSourceFromStorage = () => {
  if (!botId.value) return null;
  const storageKey = `analytics-source-${botId.value}`;
  const stored = localStorage.getItem(storageKey);
  return SOURCES.includes(stored) ? stored : null;
};

/**
 * Salva la sorgente di analytics in localStorage
 */
const saveAnalyticSourceToStorage = (source) => {
  if (!botId.value) return;
  const storageKey = `analytics-source-${botId.value}`;
  localStorage.setItem(storageKey, source);
  Console.log(`[Analytics] Saved analytics source '${source}' to localStorage for botId ${botId.value}`);
};

const fetchAnalytics = async () => {
  loading.value = true;
  error.value = null;

  try {
    // Get user's timezone offset in minutes (e.g., -120 for UTC+2 CEST)
    const timezoneOffset = new Date().getTimezoneOffset();

    const params = {
      range: selectedRange.value,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timezoneOffset: timezoneOffset
    };
    if (analyticSource.value) params.analyticSource = analyticSource.value;
    
    if (selectedRange.value === 'custom') {
      if (!customFrom.value || !customTo.value) {
        error.value = t('botEditor.analytics.customRangeError');
        return;
      }
      params.from = customFrom.value;
      params.to = customTo.value;
    }

    const response = await api.get(`/workspaces/${workspaceId.value}/bots/${botId.value}/analytics`, { params });
    analytics.value = response.data.data;

    // Nessuna scelta: si adotta la sorgente decisa dal server, senza salvarla né rifare la richiesta
    if (!analyticSource.value && response.data.data.analyticSource) {
      adoptedSource = response.data.data.analyticSource;
      analyticSource.value = adoptedSource;
    }
    
    // Aggiorna i flag dopo la risposta API
    hasMultipleVersions.value = response.data.data.hasProductionBot || response.data.data.hasTestBot;
    botType.value = response.data.data.botType;
  } catch (err) {
    Console.error('Error fetching analytics:', err);
    error.value = err.response?.data?.error?.message || t('botEditor.analytics.loadError');
  } finally {
    loading.value = false;
  }
};

const formatDuration = (ms) => {
  if (!ms || ms === 0) return '0ms';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString(locale.value, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// Lo stesso tool può comparire su due server: la chiave di riga include tipo e server
const topToolRows = computed(() => (analytics.value?.topTools ?? []).slice(0, 5).map((tool) => ({
  ...tool,
  rowKey: `${tool.toolType}:${tool.serverName ?? ''}:${tool.toolName}`,
})));

const flowTriggersTooltip = (flow) => t('botEditor.analytics.topFlows.triggers', {
  intent: flow.triggers?.intent ?? 0,
  initial: flow.triggers?.initial ?? 0,
  reentry: flow.triggers?.reentry ?? 0,
});

/** Formats a 0-1 match score as a whole percentage, e.g. 0.874 → "87%". */
const formatScore = (score) => `${(score * 100).toFixed(0)}%`;

/**
 * Pass-through for the centred numeric columns. PrimeVue lays the header cell out as
 * a flex row, so `text-align` on the `<th>` does not move the title: centre the flex
 * content instead. The body cells are centred by the Column's inline `style`, which
 * (unlike a Tailwind class) wins over PrimeVue's unlayered `td { text-align: start }`.
 */
const centeredHeaderPt = { columnHeaderContent: { class: 'justify-center' } };

// Watcher per il cambio della sorgente di analytics
watch(analyticSource, (value) => {
  if (value === adoptedSource) { adoptedSource = null; return; }
  saveAnalyticSourceToStorage(analyticSource.value);
  fetchAnalytics();
});

onMounted(() => {
  // Carica la sorgente di analytics da localStorage
  // Il valore salvato (se c'è) parte come scelta dell'utente: il watch non è ancora
  // armato dall'adozione, ma il set deve restare silenzioso
  const savedSource = loadAnalyticSourceFromStorage();
  if (savedSource) { adoptedSource = savedSource; analyticSource.value = savedSource; }

  // Fetch iniziale con la sorgente caricata
  fetchAnalytics();
});
</script>

<style scoped>

@reference "../../style.css";

.analytics-container {
  @apply w-full;
}

.analytics-header {
  @apply mb-4;
}

.stat-card {
  @apply bg-gray-800/50 border border-gray-700 rounded-lg p-3 flex items-start gap-3 hover:border-gray-600 transition;
}

.stat-icon {
  @apply w-10 h-10 rounded-lg flex items-center justify-center shrink-0;
}

.stat-content {
  @apply flex-1 min-w-0;
}

.stat-label {
  @apply text-xs text-gray-400 mb-0.5 truncate;
}

.stat-value {
  @apply text-2xl font-bold text-white;
}

.stat-value-small {
  @apply text-lg font-bold text-white;
}

.analytics-section {
  @apply bg-gray-800/50 border border-gray-700 rounded-lg p-4;
}

.section-title {
  @apply text-base font-semibold mb-3 text-cyan-400;
}
</style>
